import { DestroyRef, Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActiveProjectService } from '@app/shared/services/active-project.service';
import { NotificationsService } from '@app/shared/services/notifications.service';
import { timelineWindow } from '@app/shared/ui/timeline/timeline-days.util';
import { ProffeoUiDateRange } from '@app/shared/ui/types/date-picker.types';
import { scheduleControlDay, scheduleDayControl, schedulePeriod } from '@app/shared/utils/task-schedule.util';
import { filter, timer } from 'rxjs';

/** Route-scoped local timeline presentation only. Canonical tasks remain owned by the existing list/data services. */
@Injectable()
export class TaskScheduleActionsService {
  private readonly project = inject(ActiveProjectService);
  private readonly notifications = inject(NotificationsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly selectedWindow = signal<{ from: string; to: string } | null>(null);
  private readonly selectedAnchor = signal<string | null>(null);
  private readonly currentDay = signal(scheduleControlDay(new Date()));
  public readonly todayDay = this.currentDay.asReadonly();
  public readonly periodMode = signal<'week' | 'month'>('month');
  public readonly anchor = computed(() => this.selectedAnchor() ?? this.todayDay());
  public readonly period = computed(() => this.selectedWindow() ?? schedulePeriod(this.anchor(), this.periodMode()));
  public readonly rangeControl = computed((): ProffeoUiDateRange => ({
    start: scheduleDayControl(this.period().from),
    end: scheduleDayControl(this.period().to)
  }));
  public readonly invalidWindow = signal(false);
  public readonly isCustomWindow = computed(() => this.selectedWindow() !== null);

  public constructor() {
    effect(() => {
      this.project.activeProjectId();
      untracked(() => this.resetWindow());
    });
    this.notifications.notifications$
      .pipe(
        filter(event => event.eventType === 'UserSignedOut' || event.eventType === 'ActiveCompanySet'),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.resetWindow());
    // One clock per route, shared by axis and all rows; unchanged days do not trigger rendering.
    timer(0, 60_000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.currentDay.set(scheduleControlDay(new Date())));
  }
  private resetWindow(): void {
    this.selectedAnchor.set(null);
    this.selectedWindow.set(null);
    this.invalidWindow.set(false);
  }
  public setPeriodMode(value: 'week' | 'month'): void {
    if (value !== 'week' && value !== 'month') return;
    this.selectedWindow.set(null);
    this.invalidWindow.set(false);
    this.periodMode.set(value);
  }
  public setRange(value: ProffeoUiDateRange): void {
    this.invalidWindow.set(false);
    if (!value.start && !value.end) {
      this.selectedWindow.set(null);
      return;
    }
    if (!value.start || !value.end) return;
    try {
      const from = scheduleControlDay(value.start),
        to = scheduleControlDay(value.end);
      timelineWindow(from, to);
      this.selectedAnchor.set(from);
      this.selectedWindow.set({ from, to });
    } catch {
      this.invalidWindow.set(true);
    }
  }
  public movePeriod(delta: number): void {
    this.invalidWindow.set(false);
    if (this.selectedWindow()) {
      const window = this.period(),
        count = timelineWindow(window.from, window.to).count;
      const start = scheduleDayControl(window.from)!,
        end = scheduleDayControl(window.to)!;
      start.setDate(start.getDate() + delta * count);
      end.setDate(end.getDate() + delta * count);
      this.setRange({ start, end });
      return;
    }
    const date = scheduleDayControl(this.period().from)!;
    if (this.periodMode() === 'month') date.setMonth(date.getMonth() + delta);
    else date.setDate(date.getDate() + delta * 7);
    this.selectedAnchor.set(scheduleControlDay(date));
  }
}
